import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { PoleEmploiJobList, PoleEmploiJobResponse, toJobList } from '@models/jobs/job-preview';
import { PoleEmploiRomeLabel } from '@models/trainings/codes-romes';
import { Observable } from 'rxjs';
import { map, timeout } from 'rxjs/operators';

export const searchQueryParams: Record<string, string> = {
  keywords: 'motsCles',
  city: 'commune',
  department: 'departement',
  region: 'region',
  contract: 'typeContrat',
  duration: 'tempsPlein',
  range: 'range',
  nature: 'natureContrat',
  includeNearby: 'inclureLimitrophes',
  distance: 'distance',
  experience: 'experience',
  domain: 'grandDomaine',
  minWorkHours: 'dureeHebdoMin',
  maxWorkHours: 'dureeHebdoMax',
  dureeContratMin: 'dureeContratMin',
  dureeContratMax: 'dureeContratMax',
  romes: 'codeROME',
};

interface Payload {
  keywords?: string;
  city?: string;
  department?: string;
  region?: string;
  contract?: string;
  duration?: any;
  minWorkHours?: number;
  maxWorkHours?: number;
  nature?: string;
  range?: string;
  distance?: string;
  includeNearby: boolean;
  experience?: string;
  domain?: string;
  dureeContratMin?: string;
  dureeContratMax?: string;
  romes?: string[];
  tempsPlein?: number;
}

type StudentJobsPayload = Pick<
  Payload,
  // tslint:disable-next-line: max-union-size
  'keywords' | 'region' | 'city' | 'department' | 'distance' | 'minWorkHours' | 'maxWorkHours' | 'range'
>;

@Injectable({ providedIn: 'root' })
export class PoleEmploiWebservice {
  constructor(private readonly http: HttpClient) {}

  getJobOffer(offerId: string): Observable<any> {
    return this.http
      .get<any>(
        `${environment.api.poleEmploi}/partenaire/offresdemploi-operationsnationales/v1/offresoperations/${offerId}`
      )
      .pipe(timeout(3000));
  }

  searchJobs(payload?: Payload): Observable<PoleEmploiJobList> {
    let cleanPayload!: Payload;

    if (payload) {
      cleanPayload = this.cleanPayload(payload);
    }

    let params = new HttpParams();

    if (cleanPayload) {
      params = this.setHttpParamsFromPayload(cleanPayload);
    }

    return this.http
      .get<PoleEmploiJobResponse>(
        `${environment.api.poleEmploi}/partenaire/offresdemploi-operationsnationales/v1/offresoperations/search`,
        {
          params,
        }
      )
      .pipe(timeout(3000), map(toJobList));
  }

  searchStudentJobs(payload?: StudentJobsPayload): Observable<PoleEmploiJobList> {
    const staticParams: Payload = {
      duration: 'false',
      nature: 'E1',
      includeNearby: true,
    };
    let cleanPayload: Payload = {
      ...payload,
      ...staticParams,
    };

    if (!cleanPayload.maxWorkHours) {
      cleanPayload = { ...cleanPayload, maxWorkHours: 1599 };
    }

    cleanPayload = this.cleanPayload(cleanPayload);

    const params = this.setHttpParamsFromPayload(cleanPayload);

    return this.http
      .get<PoleEmploiJobResponse>(
        `${environment.api.poleEmploi}/partenaire/offresdemploi-operationsnationales/v1/offresoperations/search`,
        {
          params,
        }
      )
      .pipe(timeout(3000), map(toJobList));
  }

  getSummerJobOffer(offerId: string): Observable<any> {
    return this.http
      .get<any>(`${environment.api.poleEmploi}/partenaire/offresdemploi/v2/offres/${offerId}`)
      .pipe(timeout(3000));
  }

  searchSummerJobs(payload?: Payload): Observable<PoleEmploiJobList> {
    let cleanPayload!: Payload;

    if (payload) {
      cleanPayload = this.cleanPayload(payload);
    }

    let params = new HttpParams();

    if (cleanPayload) {
      params = this.setHttpParamsFromPayload(cleanPayload);
    }

    return this.http
      .get<PoleEmploiJobResponse>(`${environment.api.poleEmploi}/partenaire/offresdemploi/v2/offres/search`, {
        params,
      })
      .pipe(timeout(3000), map(toJobList));
  }

  getRomesLabel(codeRomes: string[]): Observable<PoleEmploiRomeLabel[]> {
    const params = new HttpParams().set('q', codeRomes.join(' '));

    return this.http.get<PoleEmploiRomeLabel[]>(`${environment.api.poleEmploi}/partenaire/rome/v1/appellation`, {
      params,
    });
  }

  private cleanPayload(payload: Payload): Payload {
    return (Object.keys(payload) as (keyof Payload)[]).reduce<Payload>(
      (acc: Payload, key: keyof Payload) => {
        return { ...acc, ...(payload[key] ? { [key]: payload[key] } : {}) };
      },
      { includeNearby: false }
    );
  }

  private setHttpParamsFromPayload(payload: Payload): HttpParams {
    let params = new HttpParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (!!searchQueryParams && !!searchQueryParams[key] && !!value) {
        params = params.set(searchQueryParams[key], value.toString());
      }
    });

    return params;
  }
}
